import {
  ChangeDetectorRef,
  Component,
  Input,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { Router } from '@angular/router';
import { UAMAuthService } from 'src/app/shared/services/uam-auth.service';
import { filter } from 'rxjs/operators';
import { NavigationEnd } from '@angular/router';
import { SidebarStateService } from 'src/app/shared/services/sidebar-state.service';
import { SubscriptionHelper } from 'src/app/shared/helpers/subscription.helper';

type NavigationCategory =
  | 'builders'
  | 'management'
  | 'data-explorer'
  | 'lookup'
  | 'applications'
  | 'admin';

export enum CategoryPriority {
  MANAGEMENT_TOOLS = 1,
  APPLICATIONS = 2,
  LOOKUP_TOOLS = 3,
  ADMIN_TOOLS = 4,
  BUILDER_TOOLS = 5,
}

interface SubCategoryGroup {
  subCategoryTitle: string;
  items: NavigationItem[];
}

interface CategorySection {
  category: NavigationCategory;
  subCategoryGroups: SubCategoryGroup[];
  itemsWithoutSubCategory: NavigationItem[];
}

interface NavigationItem {
  name: string;
  icon: string;
  routes: string[];
  roles: string[];
  category?: NavigationCategory;
  subCategory?: string;
  subCategoryTitle?: string;
  isDisabled?: boolean;
  sidebarState?: 'open' | 'closed';
}

@Component({
  selector: 'app-sidebar',
  templateUrl: './sidebar.component.html',
  styleUrls: ['./sidebar.component.scss'],
})
export class SidebarComponent implements OnInit, OnDestroy {
  currentUser: any = {};
  @Input() isSlimMode = false;
  @Input() set forceSidebarState(state: 'open' | 'closed' | null) {
    if (state) {
      this.isCollapsed = state === 'closed';
    }
  }

  private subsMgr = new SubscriptionHelper();

  readonly categories: NavigationCategory[] = [
    'management',
    'applications',
    'lookup',
    'admin',
    'builders',
  ];

  private static readonly CATEGORY_PRIORITY: Record<string, number> = {
    management: CategoryPriority.MANAGEMENT_TOOLS,
    applications: CategoryPriority.APPLICATIONS,
    lookup: CategoryPriority.LOOKUP_TOOLS,
    admin: CategoryPriority.ADMIN_TOOLS,
    builders: CategoryPriority.BUILDER_TOOLS,
  };

  private static readonly CATEGORY_ICONS: Record<NavigationCategory, string> = {
    management: 'settings',
    lookup: 'search',
    admin: 'user-round-check',
    applications: 'layout-grid',
    builders: 'wrench',
    'data-explorer': 'bar-chart-2',
  };

  private static readonly SUBCATEGORY_TITLES: Record<string, string> = {
    surveys: 'Surveys & Events',
    forms: 'Forms & Data',
  };

  private static readonly SUBCATEGORY_ORDER: Record<string, number> = {
    'Surveys & Events': 1,
    'Forms & Data': 2,
  };

  navigation: NavigationItem[] = [
    {
      name: 'Home',
      icon: 'home',
      routes: ['/admin'],
      roles: [
        'standalone',
        'pavcon',
        'organization-admin',
        'organization-user',
        'organization-base',
        'base',
        'base-seo',
        'pavcon-user',
      ],
      sidebarState: 'open',
    },
    // Management Tools - Surveys & Events
    {
      name: 'Surveys Builder',
      icon: 'clipboard-list',
      routes: ['/admin/surveys', '/admin/survey'],
      roles: ['pavcon', 'organization-admin', 'organization-base'],
      category: 'management',
      subCategory: 'surveys',
    },
    // Management Tools - Forms & Data
    {
      name: 'Forms Builder',
      icon: 'hammer',
      routes: ['/admin/form-builder', '/admin/form-editor'],
      roles: ['pavcon'],
      category: 'management',
      subCategory: 'forms',
      sidebarState: 'closed',
    },

    // Lookup Tools
    {
      name: 'Artifacts Lookup',
      icon: 'search',
      routes: ['/admin/search-artifacts'],
      roles: ['pavcon', 'pavcon-user'],
      category: 'lookup',
    },
    {
      name: 'Forms Data Lookup',
      icon: 'clipboard-type',
      routes: ['/admin/forms-data-lookup'],
      roles: ['pavcon', 'pavcon-user'],
      category: 'lookup',
    },
    // Management Tools - Surveys & Events
    {
      name: 'Sessions',
      icon: 'calendar',
      routes: ['/admin/sessions'],
      roles: [
        'pavcon',
        'organization-admin',
        'organization-user',
        'organization-base',
      ],
      category: 'management',
      subCategory: 'surveys',
    },
    {
      name: 'Surveys Insights Hub',
      icon: 'database',
      routes: ['/admin/results'],
      roles: [
        'pavcon',
        'organization-admin',
        'organization-user',
        'organization-base',
      ],
      category: 'management',
      subCategory: 'surveys',
    },
    // Management Tools - Forms & Data
    {
      name: 'Forms Insights Hub',
      icon: 'database',
      routes: ['/admin/forms-insights-hub'],
      roles: [
        'pavcon',
        'organization-admin',
        'organization-user',
        'organization-base',
      ],
      category: 'management',
      subCategory: 'forms',
      sidebarState: 'closed',
    },
    {
      name: 'Projects Manager',
      icon: 'folder',
      routes: ['/admin/project-manager'],
      roles: ['pavcon'],
      category: 'admin',
    },
    {
      name: 'Users Permissions',
      icon: 'shield',
      routes: ['/admin/user-manager'],
      roles: ['pavcon'],
      category: 'admin',
    },
    {
      name: 'Roles & Features Map',
      icon: 'map',
      routes: ['/admin/roles-permissions'],
      roles: ['pavcon'],
      category: 'admin',
    },
    {
      name: 'Review Access Request',
      icon: 'key-round',
      routes: ['/admin/review-access-request'],
      roles: ['pavcon'],
      category: 'admin',
    },

    // Applications
    {
      name: 'Forms',
      icon: 'file-text',
      routes: ['/admin/assignments', '/admin/assignments/workarea'],
      roles: ['standalone', 'pavcon', 'base', 'base-seo', 'organization-base'],
      category: 'applications',
    },
  ];

  isCollapsed = false;
  collapsedSubCategories = new Set<string>();

  constructor(
    private router: Router,
    private uamService: UAMAuthService,
    private sidebarStateService: SidebarStateService,
    private cdr: ChangeDetectorRef
  ) {
    this.subsMgr.add(
      this.router.events
        .pipe(filter((event) => event instanceof NavigationEnd))
        .subscribe(() => {
          this.checkRouteConfiguration();
          this.autoExpandActiveSubCategories();
        })
    );

    this.subsMgr.add(
      this.sidebarStateService.sidebarState$.subscribe((state) => {
        if (state) {
          setTimeout(() => {
            this.isCollapsed = state === 'closed';
            this.cdr.detectChanges();
          }, 0);
        }
      })
    );
  }

  ngOnInit() {
    this.currentUser = this.uamService.getAuthenticatedUser();
    this.autoExpandActiveSubCategories();
  }

  ngOnDestroy(): void {
    this.subsMgr.destroy();
  }

  private checkRouteConfiguration() {
    const currentUrl = this.router.url;
    const matchingNav = this.navigation.find((nav) =>
      nav.routes.some((route) => currentUrl.includes(route))
    );

    if (matchingNav?.sidebarState) {
      this.isCollapsed = matchingNav.sidebarState === 'closed';
    }
  }

  haveAccess(nav: NavigationItem): boolean {
    const userRole =
      this.currentUser?.['custom:warfighter_role']?.toLowerCase();
    return nav.roles?.some((role) => role.toLowerCase() === userRole);
  }

  getNavigationByCategory(category?: NavigationCategory): NavigationItem[] {
    return this.navigation.filter((item) => item.category === category);
  }

  getCategoryTitle(category: NavigationCategory): string {
    const titles: Record<NavigationCategory, string> = {
      builders: 'Builder Tools',
      management: 'Management Tools',
      'data-explorer': 'Data Explorer',
      lookup: 'Lookup Tools',
      applications: 'SAFiRE for GPs',
      admin: 'Admin Tools',
    };
    return titles[category];
  }

  getCategoryIcon(category: NavigationCategory): string {
    return SidebarComponent.CATEGORY_ICONS[category] || 'circle';
  }

  getSubCategoryTitle(subCategory: string | undefined): string | undefined {
    if (!subCategory) return undefined;
    return SidebarComponent.SUBCATEGORY_TITLES[subCategory];
  }

  private getCategoryPriority(category: string): number {
    return SidebarComponent.CATEGORY_PRIORITY[category] || 999;
  }

  private groupBySubCategory(items: NavigationItem[]): {
    groups: SubCategoryGroup[];
    withoutSubCategory: NavigationItem[];
  } {
    const groupsMap = new Map<string, NavigationItem[]>();
    const withoutSubCategory: NavigationItem[] = [];

    items.forEach((item) => {
      if (item.subCategory) {
        const subCategoryTitle = this.getSubCategoryTitle(item.subCategory);
        if (subCategoryTitle) {
          const key = subCategoryTitle;
          if (!groupsMap.has(key)) {
            groupsMap.set(key, []);
          }
          groupsMap.get(key)!.push(item);
        } else {
          withoutSubCategory.push(item);
        }
      } else {
        withoutSubCategory.push(item);
      }
    });

    const groups: SubCategoryGroup[] = Array.from(groupsMap.entries()).map(
      ([subCategoryTitle, items]) => ({
        subCategoryTitle,
        items,
      })
    );

    groups.sort((a, b) => {
      const orderA =
        SidebarComponent.SUBCATEGORY_ORDER[a.subCategoryTitle] || 999;
      const orderB =
        SidebarComponent.SUBCATEGORY_ORDER[b.subCategoryTitle] || 999;
      return orderA - orderB;
    });

    return { groups, withoutSubCategory };
  }

  getNavigationSections(): CategorySection[] {
    const sections = this.categories
      .map((category) => {
        const items = this.getNavigationByCategory(category).filter((item) =>
          this.haveAccess(item)
        );

        if (items.length === 0) {
          return null;
        }

        const { groups, withoutSubCategory } = this.groupBySubCategory(items);

        return {
          category,
          subCategoryGroups: groups,
          itemsWithoutSubCategory: withoutSubCategory,
        };
      })
      .filter((section): section is CategorySection => section !== null);

    // Sort sections based on priority
    return sections.sort((a, b) => {
      const priorityA = this.getCategoryPriority(a.category);
      const priorityB = this.getCategoryPriority(b.category);
      return priorityA - priorityB;
    });
  }

  hasCategoryItems(category: NavigationCategory): boolean {
    return this.getNavigationByCategory(category).some((nav) =>
      this.haveAccess(nav)
    );
  }

  isActive(paths: string[]): boolean {
    return paths.some((path) => this.router.url.includes(path));
  }

  hasActiveItemInSubCategory(items: NavigationItem[]): boolean {
    return items.some(
      (item) => this.haveAccess(item) && this.isActive(item.routes)
    );
  }

  toggleSubCategory(subCategoryTitle: string): void {
    if (this.collapsedSubCategories.has(subCategoryTitle)) {
      this.collapsedSubCategories.delete(subCategoryTitle);
    } else {
      this.collapsedSubCategories.add(subCategoryTitle);
    }
  }

  isSubCategoryCollapsed(
    subCategoryTitle: string,
    items?: NavigationItem[]
  ): boolean {
    // Auto-expand if has active item (override collapsed state)
    if (items && this.hasActiveItemInSubCategory(items)) {
      return false;
    }
    return this.collapsedSubCategories.has(subCategoryTitle);
  }

  shouldExpandSubCategory(items: NavigationItem[]): boolean {
    return this.hasActiveItemInSubCategory(items);
  }

  autoExpandActiveSubCategories(): void {
    const sections = this.getNavigationSections();
    sections.forEach((section) => {
      section.subCategoryGroups.forEach((group) => {
        if (this.shouldExpandSubCategory(group.items)) {
          // Auto-expand if any item is active
          this.collapsedSubCategories.delete(group.subCategoryTitle);
        }
      });
    });
  }

  onNavigationClick(nav: NavigationItem): void {
    if (nav.routes?.length) {
      this.router.navigate([nav.routes[0]]);
    }
  }

  toggleSidebar() {
    this.isCollapsed = !this.isCollapsed;
  }
}
